import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { AppStore, useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { uuidModule } from "@/modules/uuid";
import {
  MdsDropdownIconButton,
  MdsDropdownContentList,
  MdsDropdownItemKind,
} from "@/design-system/components/dropdown";
import { UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { MDSSection, MDSSectionSlotName } from "@/mds/components/section";
import { MdsItemListCarouselRow } from "@/design-system/components/item-list/rows/MdsItemListCarouselRow";
import {
  MdsCarouselItem,
  MdsCarouselItemVariant,
  MdsItemListCarouselRowData,
  MdsItemListRowType,
} from "@/design-system/components/item-list/types";
import { EventContext } from "@/domains/metrics/context";
import styled from "@emotion/styled";

export const NotesListPageHotButtonsSection = observer(() => {
  const { store } = useAppStore();

  const dropdownContentList: MdsDropdownContentList = {
    items: [
      {
        id: "manage-templates",
        kind: MdsDropdownItemKind.Button,
        label: "Manage templates",
        iconKind: MdsIconKind.Settings,
        onClick: () => store.navigation.goToTemplates(),
      },
    ],
  };

  return (
    <Wrapper>
      <MDSSection title="Create a new note">
        <div slot={MDSSectionSlotName.HeaderRightAddon}>
          <MdsDropdownIconButton
            placement="below-right-alignment"
            contentList={dropdownContentList}
          />
        </div>
        <MdsItemListCarouselRow data={getCarouselData({ store })} />
      </MDSSection>
    </Wrapper>
  );
});

const getCarouselData = ({ store }: { store: AppStore }): MdsItemListCarouselRowData => {
  const isAudioCaptureEnabled = store.spaceAccountFeatureFlags.getFlagStatus("audio-capture");
  const templates = store.templates.all.slice(0, 100);

  // Create carousel items
  const carouselItems: MdsCarouselItem[] = [
    // Blank Note button
    {
      id: "blank-note",
      primaryLabel: "Blank Note",
      variant: MdsCarouselItemVariant.Item,
      icon: () => <MdsIcon kind={MdsIconKind.EmptyDocument} />,
      onClick: () => store.notes.composeNewNote({ eventContext: EventContext.NotesActions }),
    },
    {
      id: "voice-note",
      primaryLabel: "Voice Note",
      variant: MdsCarouselItemVariant.Item,
      icon: () => <MdsIcon kind={MdsIconKind.Microphone} />,
      onClick: () =>
        store.notes.composeNewNote({
          eventContext: EventContext.NotesActions,
          autoStartVoice: true,
        }),
      isHidden: !isAudioCaptureEnabled || store.audioCapture.isIndicatorVisible,
    },
    // Template buttons
    ...templates.map(template => ({
      id: template.id,
      primaryLabel: template.title || UNTITLED_TEMPLATE_TITLE,
      secondaryLabel: "Use template",
      variant: MdsCarouselItemVariant.Item,
      icon: () => <MdsIcon kind={MdsIconKind.Document} />,
      onClick: async ({ itemId }: { itemId: string }) => {
        const noteId = uuidModule.generate();
        await store.templates.createNewNoteUsingTemplate({
          noteId,
          templateId: itemId,
        });
        store.navigation.goToNote({ noteId });
      },
    })),
    // Add template button
    {
      id: "add-template",
      primaryLabel: "Add a template",
      secondaryLabel: "Format notes consistently",
      variant: MdsCarouselItemVariant.Other,
      icon: () => <MdsIcon kind={MdsIconKind.Settings} />,
      onClick: async () => {
        const templateId = uuidModule.generate();
        await store.templates.createTemplate({ templateId });
        await store.templates.getAsync(templateId);
        store.navigation.goToTemplate({ templateId });
      },
    },
  ];

  const carouselData: MdsItemListCarouselRowData = {
    key: "collections-view-carousel",
    type: MdsItemListRowType.Carousel,
    payload: {
      items: carouselItems,
    },
  };

  return carouselData;
};

const Wrapper = styled.div(() => ({
  padding: "0 16px",
}));
